import * as Yup from "yup";
import { RobotExecutionsService as Service } from '../services/robot-executions.service';
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { PointerEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ErrorReason } from '../models/error-reason.model';
import { formatLocalizedDateTime } from 'common/helpers/dateUtils';
import { SuspectReason as Model } from "../models/suspect-reason.model";
import { useFormik } from 'formik';
import { RobotExecutionsErrorReason, RobotExecutionsSuspectReason } from '../models/robot-executions.model';
import { AgentLogStatus } from 'app/dashboard/model/dashboard.model';


interface SuspectDescriptionModalProps {
    agentLog: { id: number, suspectReason?: RobotExecutionsSuspectReason, errorReason?: RobotExecutionsErrorReason };
    onClose: () => void;
}

export default function SuspectDescriptionModal(props: SuspectDescriptionModalProps) {
    const { t } = useTranslation();
    const show = props.agentLog.id > 0;

    const newStatus = () => {
        if (props.agentLog.errorReason != null) return AgentLogStatus.Error;
        return props.agentLog.suspectReason != null ? AgentLogStatus.Success : undefined;
    }

    const initialData: Model = {
        agentLogId: props.agentLog.id,
        reason: props.agentLog.suspectReason?.reason ?? '',
        newStatus: newStatus(),
        errorReason: props.agentLog.errorReason?.reason ?? '',
        errorReasonId: props.agentLog.errorReason?.errorReasonId ?? 0
    }

    const [errorReasonsArray, setErrorReasonsArray] = useState<ErrorReason[]>([]);

    const formSchema = Yup.object().shape({
        agentLogId: Yup.number()
            .required(t("crud.validators.requiredField")),
        reason: Yup.string()
            .required(t("crud.validators.requiredField"))
            .min(5, t("crud.validators.minLength_5"))
            .max(1024, t("crud.validators.maxLength_1024")),
        newStatus: Yup.number()
            .required(t("crud.validators.requiredField")),
        errorReasonId: Yup.number()
            .when('newStatus',
                {
                    is: AgentLogStatus.Error,
                    then: Yup.number().required(t("crud.validators.requiredField")),
                    otherwise: Yup.number().notRequired()
                }),
        errorReason: Yup.string()
            .when('newStatus',
                {
                    is: AgentLogStatus.Error,
                    then: Yup.string()
                        .required(t("crud.validators.requiredField"))
                        .min(5, t("crud.validators.minLength_5"))
                        .max(1024, t("crud.validators.maxLength_1024")),
                    otherwise: Yup.string().notRequired()
                }),

    });

    const onSubmit = async () => {
        try {
            const result = await Service.createSuspectReason(formik.values);
            if (result && !result?.hasErrors) {
                toast.success(`${t("crud.create.successMessage")}`);
                props.onClose();
            } else {
                toast.error(`${t(`error.${result?.errorCode}`)}`);
            }
        } catch (error) {
            toast.error(`${t("crud.update.errorMessage")}`);
        }
    };

    const formik = useFormik<Model>({
        enableReinitialize: true,
        initialValues: initialData,
        validationSchema: formSchema,
        onSubmit: onSubmit
    });

    const fetchErrorType = async () => {
        try {
            const response = await Service.getErrorReasons();
            if (response && !response.hasErrors) {
                setErrorReasonsArray(response.data);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
        }
    };

    const onNewStatusChange = async (e: SelectChangeEvent<AgentLogStatus>) => {
        const newStatus: AgentLogStatus = e.target.value as AgentLogStatus;
        if (newStatus == AgentLogStatus.Success) {
            await formik.setFieldValue('errorReasonId', 0);
            await formik.setFieldValue('errorReason', '');
        }
        formik.handleChange(e);
    }

    useEffect(() => {
        fetchErrorType();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <>
        <Modal show={show} onHide={props.onClose} size="lg" >
            <form onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}>
                <div className="card">
                    <div className="card-body">
                        <div className="mb-3">
                            <h3>{t("robotExecutions.suspectReason.title")}</h3>
                        </div>
                        {
                            props.agentLog.suspectReason != null &&
                            <>
                                <div className="row mb-3">
                                    <div className="col-md-6 mt-3 fw-bold row">
                                        <div>{t("robotExecutions.suspectReason.createdBy")}:</div>
                                        <div>{props.agentLog.suspectReason.createdByUser}</div>
                                    </div>
                                    <div className="col-md-6 mt-3 fw-bold row">
                                        <div>{t("robotExecutions.suspectReason.when")}:</div>
                                        <div>{formatLocalizedDateTime(props.agentLog.suspectReason.when.toString(), 'lll')}</div>
                                    </div>
                                </div>
                            </>
                        }

                        <div className="col-md-12">
                            <FormControl variant="filled" style={{ width: "100%" }}>
                                <label className="form-label required me-3">{t("robotExecutions.suspectReason.description")}</label>
                                <textarea
                                    className="form-control form-control-lg form-control-solid"
                                    name="reason"
                                    onChange={formik.handleChange}
                                    disabled={props.agentLog.suspectReason != null}
                                    value={formik.values.reason ?? ''}
                                    onBlur={formik.handleBlur} />
                                {formik.touched.reason && formik.errors.reason && (
                                    <div className="mt-3 text-danger fw-bold">{formik.errors.reason}</div>
                                )}
                            </FormControl>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <FormControl variant="filled" style={{ width: "100%" }}>
                                    <label className="form-label required me-3">{t("robotExecutions.suspectReason.newStatus")}</label>
                                    <Select
                                        name="newStatus"
                                        MenuProps={{ disableScrollLock: true }}
                                        value={formik.values.newStatus ?? ''}
                                        disabled={props.agentLog.suspectReason != null}
                                        onChange={onNewStatusChange}
                                    >
                                        <MenuItem value={AgentLogStatus.Success} key={AgentLogStatus.Success}>
                                            {t("robotExecutions.suspectReason.newStatusSuccess")}
                                        </MenuItem>
                                        <MenuItem value={AgentLogStatus.Error} key={AgentLogStatus.Error}>
                                            {t("robotExecutions.suspectReason.newStatusError")}
                                        </MenuItem>
                                    </Select>
                                    {formik.touched.newStatus && formik.errors.newStatus && (
                                        <div className="mt-3 text-danger fw-bold">{formik.errors.newStatus}</div>
                                    )}
                                </FormControl>
                            </div>


                            {
                                formik.values.newStatus == AgentLogStatus.Error &&
                                <>

                                    <div className="col-md-12">
                                        <FormControl variant="filled" style={{ width: "100%" }}>
                                            <label className="form-label required me-3">{t("robotExecutions.suspectReason.errorType")}</label>
                                            <Select
                                                name="errorReasonId"
                                                MenuProps={{ disableScrollLock: true }}
                                                value={formik.values.errorReasonId ?? 0}
                                                disabled={props.agentLog.suspectReason != null}
                                                onChange={formik.handleChange}
                                            >
                                                {errorReasonsArray?.map((value: ErrorReason) => (
                                                    <MenuItem value={value.id} key={value.id}>
                                                        {value.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.touched.errorReasonId && formik.errors.errorReasonId && (
                                                <div className="mt-3 text-danger fw-bold">{formik.errors.errorReasonId}</div>
                                            )}
                                        </FormControl>
                                    </div>
                                    <div className="col-md-12">
                                        <FormControl variant="filled" style={{ width: "100%" }}>
                                            <label className="form-label required me-3">{t("robotExecutions.suspectReason.errorReason")}</label>
                                            <textarea
                                                className="form-control form-control-lg form-control-solid"
                                                name="errorReason"
                                                onChange={formik.handleChange}
                                                disabled={props.agentLog.suspectReason != null}
                                                value={formik.values.errorReason ?? 0}
                                                onBlur={formik.handleBlur} />
                                            {formik.touched.errorReason && formik.errors.errorReason && (
                                                <div className="mt-3 text-danger fw-bold">{formik.errors.errorReason}</div>
                                            )}
                                        </FormControl>
                                    </div>
                                </>
                            }

                        </div>


                    </div>
                    <div className="card-footer">
                        <div className="row justify-content-end">
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-primary" disabled={!formik.isValid || !formik.dirty} >
                                    {t('crud.create.buttonSave')}
                                </button>
                            </div>
                            <div className="col-md-2">
                                <button type="button" className="btn btn-active-light-danger" onClick={props.onClose}>
                                    {t('crud.create.buttonCancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Modal >
    </>;
}